'use client';

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Mic, Play, Square, Trash2, Plus, Search } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ListFilters, useSearchFilter } from '@/shared/components/list';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';

// ===========================
// Types matching VoiceCatalogService.VoiceInfo
// ===========================

export interface VoiceItem {
  voiceId: string;
  name: string;
  previewUrl?: string | null;
  category?: string | null;
  isCustom?: boolean;
  language?: string | null;
  gender?: 'male' | 'female' | 'neutral' | string | null;
  addedAt?: string | null;
  /** ElevenLabs-era clone — kept for the record, never offered in a picker. */
  legacy?: boolean;
}

// ===========================
// Props
// ===========================

export interface VoiceLibraryPageProps {
  /** Fetch custom voices for the family. Returns array of VoiceItem. */
  fetchVoices: () => Promise<VoiceItem[]>;
  /** Delete a custom voice by voiceId. Returns success boolean. */
  deleteVoice: (voiceId: string) => Promise<{ success: boolean }>;
  /** Called when user clicks "Clone Voice". Parent handles navigation/dialog. */
  onCloneVoice: () => void;
}

// ===========================
// Component
// ===========================

export function VoiceLibraryPage({
  fetchVoices,
  deleteVoice,
  onCloneVoice,
}: VoiceLibraryPageProps) {
  const t = useTranslations('voices');

  const [voices, setVoices] = useState<VoiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingVoiceId, setDeletingVoiceId] = useState<string | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { searchValue, debouncedValue: searchQuery, setSearchValue } = useSearchFilter();
  const [typeFilter, setTypeFilter] = useState<'all' | 'cloned' | 'system'>('all');

  // Fetch voices on mount
  const loadVoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchVoices();
      setVoices(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [fetchVoices]);

  useEffect(() => {
    loadVoices();
  }, [loadVoices]);

  const customVoiceCount = useMemo(() => voices.filter(v => v.isCustom || v.category === 'cloned').length, [voices]);

  const filteredVoices = useMemo(() => {
    let result = voices;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((v) => v.name.toLowerCase().includes(query));
    }
    if (typeFilter !== 'all') {
      result = result.filter((v) => {
        const isCloned = v.category === 'cloned' || v.isCustom;
        return typeFilter === 'cloned' ? isCloned : !isCloned;
      });
    }
    return result;
  }, [voices, searchQuery, typeFilter]);

  // Audio playback
  const handlePlayPreview = useCallback(
    (voiceId: string, previewUrl: string) => {
      // If same voice is playing, stop it
      if (playingVoiceId === voiceId) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current = null;
        }
        setPlayingVoiceId(null);
        return;
      }

      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const audio = new Audio(mediaSrc(previewUrl));
      audioRef.current = audio;
      setPlayingVoiceId(voiceId);

      audio.play().catch(() => {
        setPlayingVoiceId(null);
      });

      audio.addEventListener('ended', () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
      });

      audio.addEventListener('error', () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
      });
    },
    [playingVoiceId]
  );

  // Delete voice
  const handleDeleteVoice = useCallback(
    async (voiceId: string) => {
      setDeletingVoiceId(voiceId);
      try {
        const result = await deleteVoice(voiceId);
        if (result.success) {
          setVoices((prev) => prev.filter((v) => v.voiceId !== voiceId));
        }
      } catch {
        // Error handling — could show a toast in production
      } finally {
        setDeletingVoiceId(null);
      }
    },
    [deleteVoice]
  );

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // ===========================
  // Loading state
  // ===========================
  if (loading) {
    return (
      <div className="space-y-3 sm:space-y-6" data-testid="voice-library-loading">
        <div className="flex items-center justify-between gap-2 mb-6">
          <div>
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-24 mt-1" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="py-4">
              <CardContent className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ===========================
  // Error state
  // ===========================
  if (error) {
    return (
      <div className="space-y-3 sm:space-y-6" data-testid="voice-library-error">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground">{t('title')}</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <p className="text-sm text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={loadVoices}>
            {t('retry')}
          </Button>
        </div>
      </div>
    );
  }

  // ===========================
  // Empty state
  // ===========================
  if (voices.length === 0) {
    return (
      <div className="space-y-3 sm:space-y-6" data-testid="voice-library-empty">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground">{t('title')}</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <Mic className="w-20 h-20 mb-4 text-muted-foreground/30 stroke-[1.5]" />
          <h3
            className="text-lg font-semibold text-foreground mb-2"
            data-testid="empty-title"
          >
            {t('empty.title')}
          </h3>
          <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
            {t('empty.description')}
          </p>
          <Button
            size="lg"
            onClick={onCloneVoice}
            data-testid="clone-voice-button"
          >
            <Plus className="w-4 h-4" />
            {t('clone')}
          </Button>
        </div>
      </div>
    );
  }

  // ===========================
  // Main state
  // ===========================
  return (
    <div className="space-y-3 sm:space-y-6" data-testid="voice-library">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground">{t('title')}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {searchQuery.trim() || typeFilter !== 'all'
              ? t('found', { count: filteredVoices.length })
              : t('found', { count: voices.length })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            onClick={onCloneVoice}
            disabled={customVoiceCount >= 5}
            data-testid="clone-voice-button"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('clone')}</span>
          </Button>
          {customVoiceCount >= 5 && (
            <p className="text-xs text-muted-foreground">{t('limitReached')}</p>
          )}
        </div>
      </div>

      {/* Filters removed — not useful with small voice count */}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {filteredVoices.map((voice) => (
          <VoiceCard
            key={voice.voiceId}
            voice={voice}
            isPlaying={playingVoiceId === voice.voiceId}
            isDeleting={deletingVoiceId === voice.voiceId}
            onPlayPreview={handlePlayPreview}
            onDelete={handleDeleteVoice}
            t={t}
          />
        ))}
      </div>

      {filteredVoices.length === 0 && voices.length > 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Search className="w-12 h-12 mb-4 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{t('emptyFiltered')}</p>
        </div>
      )}
    </div>
  );
}

// ===========================
// Sub-components
// ===========================

interface VoiceCardProps {
  voice: VoiceItem;
  isPlaying: boolean;
  isDeleting: boolean;
  onPlayPreview: (voiceId: string, previewUrl: string) => void;
  onDelete: (voiceId: string) => void;
  t: (key: string) => string;
}

function VoiceCard({
  voice,
  isPlaying,
  isDeleting,
  onPlayPreview,
  onDelete,
  t,
}: VoiceCardProps) {
  const locale = useLocale();
  const isCloned = voice.category === 'cloned' || voice.isCustom;

  return (
    <Card className="py-2 sm:py-3 hover:border-primary/50 hover:shadow-md transition-all" data-testid={`voice-card-${voice.voiceId}`}>
      <CardContent className="flex items-center gap-2 sm:gap-3">
        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10">
          <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
        </div>

        <div className="flex-1 min-w-0">
          <span
            className="text-sm sm:text-base font-medium text-foreground truncate block"
            data-testid={`voice-name-${voice.voiceId}`}
          >
            {voice.name}
          </span>
          <div className="flex items-center gap-1 mt-0.5">
            {voice.language && (
              <Badge
                variant="outline"
                className="text-[10px] px-1 py-0 uppercase"
                data-testid={`voice-language-${voice.voiceId}`}
              >
                {voice.language}
              </Badge>
            )}
            <Badge
              variant={isCloned ? 'default' : 'secondary'}
              className="text-[10px] px-1 py-0"
              data-testid={`voice-badge-${voice.voiceId}`}
            >
              {isCloned ? t('type.cloned') : t('type.system')}
            </Badge>
            {voice.legacy && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground" data-testid={`voice-legacy-${voice.voiceId}`}>
                {t('type.legacy')}
              </Badge>
            )}
            {(voice.gender === 'male' || voice.gender === 'female') && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {t(`gender.${voice.gender}`)}
              </Badge>
            )}
          </div>
          {/* Where this voice is used + when it was made — the answer to
              "what is this and what do I do with it" on a plain list. */}
          <p className="mt-0.5 text-[11px] text-muted-foreground truncate">
            {isCloned ? t('cardHint.cloned') : t('cardHint.system')}
            {voice.addedAt && ` · ${new Date(voice.addedAt).toLocaleDateString(locale)}`}
          </p>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
          {voice.previewUrl && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 sm:h-9 sm:w-9"
              onClick={() => onPlayPreview(voice.voiceId, voice.previewUrl!)}
              aria-label={isPlaying ? t('stop') : t('preview')}
              data-testid={`play-preview-${voice.voiceId}`}
            >
              {isPlaying ? (
                <Square className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              ) : (
                <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              )}
            </Button>
          )}

          {isCloned && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:h-9 sm:w-9"
                  disabled={isDeleting}
                  aria-label={t('delete')}
                  data-testid={`delete-voice-${voice.voiceId}`}
                >
                  <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('deleteConfirm.title')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('deleteConfirm.description')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('deleteConfirm.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(voice.voiceId)}
                    data-testid={`confirm-delete-${voice.voiceId}`}
                  >
                    {t('deleteConfirm.confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
